import { useCallback } from 'react';

import type { LessonAssignFragment } from '../assign/assign-lesson.graphql.generated';
import type {
  UpdateLessonBookmarkMutation,
  UpdateLessonBookmarkMutationVariables,
} from '../../lesson/lesson.graphql.generated';
import { UPDATE_LESSON_BOOKMARKS } from 'approot/lesson/lesson.graphql';
import { useLoggingMutation } from 'lib/apollo/apollo-hooks';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { useCurrentClassVar } from 'approot/classes/classes.hooks';
import { removeTypenameFromArray } from 'approot/shared/logic/remove-typename';
import type { Pluck } from 'lib/gql/pluck';

export const useBookmarkLessonActivities = ({
  lessonId,
  unitId,
  assignedData,
  onCompleted,
}: {
  lessonId: number;
  unitId: number;
  assignedData: {
    stimulusResourceIds: number[];
    workSheetPages?: number[];
    vocabularyEnPages?: boolean;
    vocabularyEsPages?: boolean;
    activitiesBookmarks?: {
      __typename: 'ActivitiesBookmarkMetaData';
      version: string;
      sectionIds: string[];
    };
  } | null;
  onCompleted?: (
    assignedData: Pluck<
      UpdateLessonBookmarkMutation,
      'updateLessonBookmark.assignedData'
    > | null,
    bookmarked: boolean
  ) => void;
}) => {
  const { showError } = useSnackbar();

  const currentClass = useCurrentClassVar();
  const classId = currentClass?.id || 0;

  const [updateLessonBookmarks, { loading, error }] = useLoggingMutation<
    UpdateLessonBookmarkMutation,
    UpdateLessonBookmarkMutationVariables
  >(UPDATE_LESSON_BOOKMARKS, {
    onCompleted: res =>
      onCompleted?.(
        res.updateLessonBookmark.assignedData,
        res.updateLessonBookmark.isAssigned
      ),
  });

  const handleBookmarkResources = useCallback(
    ({
      activitiesBookmarks,
      workSheetPages,
      stimulusResourceIds,
    }: {
      activitiesBookmarks: Pluck<
        LessonAssignFragment,
        'assignedData.activitiesBookmarks'
      >[];
      workSheetPages: number[];
      stimulusResourceIds: number[];
    }) => {
      return updateLessonBookmarks({
        variables: {
          input: {
            lessonId,
            unitId,
            workSheetPages,
            vocabularyEnPages: !!assignedData?.vocabularyEnPages,
            vocabularyEsPages: !!assignedData?.vocabularyEsPages,
            stimulusResourceIds,
            classId,
            activitiesBookmarks: removeTypenameFromArray(
              activitiesBookmarks || []
            ),
          },
        },
      }).catch(error => {
        if (error) {
          showError('There was an error updating the bookmarks.');
        }
      });
    },
    [unitId, lessonId, classId, assignedData, showError, updateLessonBookmarks]
  );

  return {
    loading,
    error,
    handleBookmarkResources,
  };
};
